import React from 'react';

import styled, { css } from 'styled-components';

import ArrowLoadingIndicator from '../../../icons/components/ArrowLoadingIndicator';
import {
  createButtonAdditionalStyle,
  createButtonDisabledStyle,
  createButtonStyle,
} from '../utils/button';

const LoadingIndicatorWrapper = styled.span`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;

const sizeVariants = {
  sm: {
    padding: css`
      padding: ${({ theme }) =>
        css`
          ${theme.sizes.xxs}px ${theme.sizes.sm}px
        `};
    `,
    typography: css`
      font-weight: 600;
      ${({ theme: { typography } }) => typography.values.paragraph1};
    `,
  },
  md: {
    padding: css`
      padding: ${({ theme }) =>
        css`
          ${theme.sizes.xs}px 18px
        `};
    `,
    typography: css`
      font-weight: 600;
      ${({ theme: { typography } }) => typography.values.paragraph2};
    `,
  },
  lg: {
    padding: 'padding: 14px 20px',
    typography: css`
      font-weight: 600;
      ${({ theme: { typography } }) => typography.values.paragraph3};
    `,
  },
};

const baseButtonStyle = css`
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  vertical-align: middle;
  width: auto;
  min-width: fit-content;
  opacity: 1;

  ${({ theme: { button }, size = 'md' }) => css`
    border-radius: ${button.borderRadius};
    ${sizeVariants[size].padding};

    & > span {
      ${sizeVariants[size].typography};
      font-family: inherit;
    }
  `}

  transition: background-color 0.16s ease-in-out, border-color 0.16s ease-in-out;

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
    `}
`;

const baseButtonLabelStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: normal;
  // Gap to give appropriate space among button children
  ${({ theme: { spacing } }) => css`
    gap: ${spacing.sizes.pixels.xxs}px;
  `}
  transition: color 0.16s ease-in-out;
`;

const StyledArrowLoadingIndicator = styled(ArrowLoadingIndicator)`
  ${({ $fill }) =>
    css`
      fill: ${$fill};
    `}
`;

const baseButton = (fillColor = { color: 'inherit' }) => ({
  loadingIndicator: (
    <LoadingIndicatorWrapper>
      <StyledArrowLoadingIndicator
        $fill={css`
          ${fillColor}
        `}
      />
    </LoadingIndicatorWrapper>
  ),
});

const baseButtonConfig = {
  baseButton,
  baseButtonStyle,
  baseButtonLabelStyle,
};

// Shared styles for variant groups
const sharedPrimaryButtonStyles = css`
  min-width: 186px;
  padding: 16px 24px;
  border: none;
  font-weight: 700;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  border-radius: 8px;
`;

const sharedSecondaryButtonStyles = css`
  min-width: 208px;
  padding: 16px 24px;
  border: 1px solid;
  background-color: transparent;
  font-weight: 700;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  border-radius: 8px;
`;

const sharedTertiaryButtonStyles = css`
  min-width: 167px;
  padding: 8px 16px;
  min-height: 32px;
  border: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  border-radius: 8px;
`;

// New button variants using Savage theme colors
const primaryDarkButtonStyles = createButtonStyle({
  ...baseButtonConfig,
  foreground: css`
    ${({ theme }) => theme.colors.palette.white}
  `,
  background: css`
    ${({ theme }) => theme.colors.palette.gunmetal}
  `,
  additionalStyles: css`
    ${sharedPrimaryButtonStyles}
    color: ${({ theme }) => theme.colors.palette.white};

    &:hover,
    &:focus,
    &:active {
      color: ${({ theme }) => theme.colors.palette.white};
      background: ${({ theme }) => theme.colors.palette.black};
    }

    ${({ disabled }) =>
      disabled &&
      css`
        cursor: not-allowed;
        opacity: 0.5;
        background: ${({ theme }) => theme.colors.palette.gunmetal};
        transition: background-color 300ms linear, border-color 300ms linear;

        &:hover,
        &:focus,
        &:active {
          background: ${({ theme }) => theme.colors.palette.gunmetal900};
        }
      `}
  `,
});

const primaryLightButtonStyles = createButtonStyle({
  ...baseButtonConfig,
  foreground: css`
    ${({ theme }) => theme.colors.palette.gunmetal}
  `,
  background: css`
    ${({ theme }) => theme.colors.palette.white}
  `,
  additionalStyles: css`
    ${sharedPrimaryButtonStyles}
    color: ${({ theme }) => theme.colors.palette.gunmetal};

    &:hover,
    &:focus,
    &:active {
      background: ${({ theme }) => theme.colors.palette.gunmetal250};
    }

    ${({ disabled }) =>
      disabled &&
      css`
        cursor: not-allowed;
        opacity: 0.5;

        &:hover,
        &:focus,
        &:active {
          background: ${({ theme }) => theme.colors.palette.white};
        }
      `}
  `,
});

const primaryLavenderButtonStyles = createButtonStyle({
  ...baseButtonConfig,
  foreground: css`
    ${({ theme }) => theme.colors.palette.black}
  `,
  background: css`
    ${({ theme }) => theme.colors.palette.lavender}
  `,
  additionalStyles: css`
    ${sharedPrimaryButtonStyles}
    color: ${({ theme }) => theme.colors.palette.black};

    &:hover,
    &:focus,
    &:active {
      background: ${({ theme }) => theme.colors.palette.lavender400};
    }

    ${({ disabled }) =>
      disabled &&
      css`
        cursor: not-allowed;
        opacity: 0.5;
        background: ${({ theme }) => theme.colors.palette.lavender};
        transition: background-color 300ms linear, border-color 300ms linear;

        &:hover,
        &:focus,
        &:active {
          background: ${({ theme }) => theme.colors.palette.lavender400};
        }
      `}
  `,
});

const secondaryDarkButtonStyles = createButtonStyle({
  ...baseButtonConfig,
  foreground: css`
    ${({ theme }) => theme.colors.palette.gunmetal}
  `,
  background: css`
    transparent
  `,
  border: {
    color: css`
      ${({ theme }) => theme.colors.palette.black}
    `,
    width: 1,
  },
  additionalStyles: css`
    ${sharedSecondaryButtonStyles}
    color: ${({ theme }) => theme.colors.palette.gunmetal};

    &:hover,
    &:focus,
    &:active {
      ${createButtonAdditionalStyle({
        foreground: css`
          ${({ theme }) => theme.colors.palette.gunmetal_50}
        `,
        borderColor: css`
          ${({ theme }) => theme.colors.palette.gunmetal_50}
        `,
        background: 'transparent',
      })}
    }

    ${({ disabled }) =>
      disabled &&
      css`
        cursor: not-allowed;
        opacity: 0.5;

        &:hover,
        &:focus,
        &:active {
          background: transparent;
        }
      `}
  `,
});

const secondaryLightButtonStyles = createButtonStyle({
  ...baseButtonConfig,
  foreground: css`
    ${({ theme }) => theme.colors.palette.white}
  `,
  background: css`
    transparent
  `,
  border: {
    color: css`
      ${({ theme }) => theme.colors.palette.white}
    `,
    width: 1,
  },
  additionalStyles: css`
    ${sharedSecondaryButtonStyles}
    color: ${({ theme }) => theme.colors.palette.white};

    &:hover,
    &:focus,
    &:active {
      background: ${({ theme }) => theme.colors.palette.gunmetal_50};
    }

    ${({ disabled }) =>
      disabled &&
      css`
        cursor: not-allowed;
        opacity: 0.5;

        &:hover,
        &:focus,
        &:active {
          background: transparent;
        }
      `}
  `,
});

const secondaryLavenderButtonStyles = createButtonStyle({
  ...baseButtonConfig,
  foreground: css`
    ${({ theme }) => theme.colors.palette.lavender600}
  `,
  background: css`
    transparent
  `,
  border: {
    color: css`
      ${({ theme }) => theme.colors.palette.lavender600}
    `,
    width: 1,
  },
  additionalStyles: css`
    ${sharedSecondaryButtonStyles}
    color: ${({ theme }) => theme.colors.palette.lavender600};

    &:hover,
    &:focus,
    &:active {
      background: ${({ theme }) => theme.colors.palette.lavender100};
      border-color: ${({ theme }) => theme.colors.palette.lavender700};
    }

    ${({ disabled }) =>
      disabled &&
      css`
        cursor: not-allowed;
        opacity: 0.5;

        &:hover,
        &:focus,
        &:active {
          background: transparent;
          border-color: ${({ theme }) => theme.colors.palette.lavender600};
        }
      `}
  `,
});

const tertiaryDarkButtonStyles = createButtonStyle({
  ...baseButtonConfig,
  foreground: css`
    ${({ theme }) => theme.colors.palette.white}
  `,
  background: css`
    ${({ theme }) => theme.colors.palette.gunmetal}
  `,
  additionalStyles: css`
    ${sharedTertiaryButtonStyles}
    color: ${({ theme }) => theme.colors.palette.white};

    &:hover,
    &:focus,
    &:active {
      background: ${({ theme }) => theme.colors.palette.gunmetal300};
    }

    ${({ disabled }) =>
      disabled &&
      css`
        cursor: not-allowed;
        opacity: 0.5;

        &:hover,
        &:focus,
        &:active {
          background: ${({ theme }) => theme.colors.palette.gunmetal};
        }
      `}
  `,
});

const tertiaryLightButtonStyles = createButtonStyle({
  ...baseButtonConfig,
  foreground: css`
    ${({ theme }) => theme.colors.palette.gunmetal}
  `,
  background: css`
    ${({ theme }) => theme.colors.palette.white}
  `,
  additionalStyles: css`
    ${sharedTertiaryButtonStyles}
    color: ${({ theme }) => theme.colors.palette.gunmetal};

    &:hover,
    &:focus,
    &:active {
      background: ${({ theme }) => theme.colors.palette.gunmetal250};
    }

    ${({ disabled }) =>
      disabled &&
      css`
        cursor: not-allowed;
        opacity: 0.5;

        &:hover,
        &:focus,
        &:active {
          background: ${({ theme }) => theme.colors.palette.white};
        }
      `}
  `,
});

const secondaryButtonStyles = createButtonStyle({
  ...baseButtonConfig,
  foreground: css`
    ${({ theme }) => theme.colors.palette.gunmetal900}
  `,
  background: css`
    ${({ theme }) => theme.colors.palette.stone}
  `,
  additionalStyles: css`
    text-transform: uppercase;
    border: none;
    border-radius: 4px;

    & > span {
      font-weight: 400;
      text-transform: uppercase;
      line-height: 1.5;
      letter-spacing: 1px;
    }

    &:hover,
    &:focus,
    &:active {
      background: ${({ theme }) => theme.colors.palette.stone350};
    }

    ${({ disabled }) =>
      disabled &&
      css`
        opacity: 50%;
        cursor: not-allowed;
      `}
  `,
});

const primaryAddToBagStyles = createButtonStyle({
  ...baseButtonConfig,
  foreground: css`
    ${({ theme }) => theme.colors.palette.white}
  `,
  background: css`
    ${({ disabled, theme }) =>
      disabled
        ? theme.colors.palette.lavender200
        : theme.colors.palette.lavender}
  `,
  additionalStyles: css`
    & > span {
      color: ${({ theme }) => theme.colors.palette.black};

      font-weight: 400;
      line-height: 1.3;
      text-transform: uppercase;
      letter-spacing: 1px;
    }

    &:hover,
    &:focus,
    &:active {
      background: ${({ theme }) => theme.colors.palette.lavender400};
    }

    ${({ disabled }) =>
      disabled &&
      css`
        cursor: not-allowed;
        & > span {
          color: ${({ theme }) => theme.colors.palette.gunmetal400};
        }

        &:hover,
        &:focus,
        &:active {
          background: ${({ theme }) => theme.colors.palette.lavender300};
        }
      `}
  `,
});

const tertiaryButtonStyles = createButtonStyle({
  ...baseButtonConfig,
  foreground: css`
    ${({ theme }) => theme.colors.palette.white}
  `,
  background: css`
    ${({ disabled, theme }) =>
      disabled
        ? theme.colors.palette.lavender200
        : theme.colors.palette.lavender}
  `,
  additionalStyles: css`
    text-transform: uppercase;

    &:hover,
    &:focus,
    &:active {
      background: ${({ theme }) => theme.colors.palette.lavender400};
    }

    ${({ disabled }) => disabled && css``}
  `,
});

const primaryButtonStyles = (injectedStyles = '') =>
  createButtonStyle({
    ...baseButtonConfig,
    foreground: css`
      ${({ disabled, theme }) =>
        disabled ? theme.colors.palette.black : theme.colors.palette.white}
    `,
    background: css`
      ${({ disabled, theme }) =>
        disabled
          ? theme.colors.palette.gunmetal350
          : theme.colors.palette.gunmetal900}
    `,
    additionalStyles: css`
      text-transform: uppercase;
      color: ${({ theme }) => theme.colors.palette.white};

      & > span {
        font-weight: 400;
        line-height: 1.3;
        text-transform: uppercase;
        letter-spacing: 1px;
      }

      &:hover,
      &:focus,
      &:active {
        color: ${({ theme }) => theme.colors.palette.white};
        background: ${({ theme }) => theme.colors.palette.black};
      }

      ${injectedStyles}

      ${({ disabled }) =>
        disabled &&
        css`
          span {
            color: ${({ theme }) => theme.colors.palette.gunmetal500};
          }

          &:hover,
          &:focus,
          &:active {
            color: ${({ theme }) => theme.colors.palette.black};
            background-color: ${({ theme }) =>
              theme.colors.palette.gunmetal450};
          }
        `}
    `,
  });

const button = {
  borderRadius: css`
    ${({ theme }) => theme.sizes.xxxs}px
  `,
  variants: {
    primary: primaryButtonStyles(),
    primaryAlt: primaryButtonStyles(),
    secondary: secondaryButtonStyles,
    tertiary: tertiaryButtonStyles,
    alternate: tertiaryButtonStyles,
    primaryAddToBag: primaryAddToBagStyles,
    gradient: tertiaryButtonStyles,

    // New SXF variant styles
    primaryDark: primaryDarkButtonStyles,
    primaryLight: primaryLightButtonStyles,
    primaryLavender: primaryLavenderButtonStyles,
    secondaryDark: secondaryDarkButtonStyles,
    secondaryLight: secondaryLightButtonStyles,
    secondaryLavender: secondaryLavenderButtonStyles,
    tertiaryDark: tertiaryDarkButtonStyles,
    tertiaryLight: tertiaryLightButtonStyles,

    white: secondaryButtonStyles,
    unstyled: {
      buttonStyle: css`
        cursor: pointer;
        width: auto;
        height: auto;
        border: none;
        margin: 0;
        padding: 0;
        font-size: inherit;
        font-weight: normal;
        background: transparent;
        color: inherit;

        ${(props) =>
          props.disabled &&
          css`
            cursor: not-allowed;
            opacity: 0.5;
          `}
      `,
      labelStyle: baseButtonLabelStyle,
    },
    link: createButtonStyle({
      ...baseButtonConfig,
      foreground: css`
        ${({ theme }) => theme.colors.palette.black}
      `,
      background: 'transparent',
      additionalStyles: css`
        padding: 0;

        ${({ disabled }) =>
          disabled &&
          createButtonDisabledStyle({
            foreground: css`
              ${({ theme }) => theme.colors.palette.gunmetal450}
            `,
            background: 'transparent',
          })}
      `,
    }),
    linkSecondary: createButtonStyle({
      ...baseButtonConfig,
      foreground: css`
        ${({ theme }) => theme.colors.palette.gunmetal}
      `,
      background: 'transparent',
      additionalStyles: css`
        padding: 0;

        & > span {
          font-weight: 400;
          text-transform: uppercase;
          line-height: 1.5;
          letter-spacing: 1px;
          text-decoration: underline;
          text-decoration-color: ${({ theme }) =>
            theme.colors.palette.gunmetal};

          &:hover,
          &:focus,
          &:active {
            text-decoration: none;
          }
        }

        &:hover,
        &:focus,
        &:active {
          ${createButtonAdditionalStyle({
            foreground: css`
              ${({ theme }) => theme.colors.palette.gunmetal}
            `,
            background: 'transparent',
          })}
        }

        ${({ disabled }) =>
          disabled &&
          createButtonDisabledStyle({
            foreground: css`
              ${({ theme }) => theme.colors.palette.gunmetal450}
            `,
            background: 'transparent',
          })}
      `,
    }),
  },
  sizeVariants,
};

export default button;
